import { defineComponent as _defineComponent } from 'vue'
import TableActionsList from '@/components/tableWithActions/TableActionsList.vue';
import SearchBox from '@/components/tableWithActions/SearchBox.vue';
import { FilterMatcher, matchIgnoringCase } from '@/utils/table';
import { getFieldPathValue, isNil } from '@/utils/misc';
import ControlledSidebar from '@/components/ControlledSidebar.vue';
import { SortOrder } from '@/models/enums/SortOrder';
import TableColumnActionsWrapper from './TableColumnActionsWrapper.vue';
import { useI18n } from '@/composables/i18n';
import { computed, ref, shallowRef, useSlots, watch } from 'vue';

type PaginationPosition = 'top' | 'bottom';
type SortIconSize = 'is-small' | 'is-medium' | 'is-large' | '';
type SearchMatcher = (row: any, searchText: string) => boolean;


export default /*#__PURE__*/_defineComponent({
  __name: 'TableWithActions2',
  props: {
    data: null,
    paginated: { type: Boolean, default: true },
    rowsPerPage: { default: 10 },
    total: null,
    paginationSimple: { type: Boolean, default: false },
    paginationPosition: { default: 'bottom' },
    defaultSortDirection: { default: SortOrder.Ascending },
    sortIcon: { default: 'arrow-up' },
    sortIconSize: { default: 'is-small' },
    checkable: { type: Boolean, default: false },
    checkedRows: null,
    filterMatchers: null,
    defaultSort: null,
    titleField: null,
    titleLabel: null,
    loading: { type: Boolean, default: false },
    hoverable: { type: Boolean, default: false },
    detailed: { type: Boolean, default: false },
    detailKey: null,
    customDetailRow: { type: Boolean, default: false },
    searchable: { type: Boolean, default: true },
    stickyHeaders: { type: Boolean, default: false },
    detailRowActions: { type: Boolean, default: false },
    backendPagination: { type: Boolean, default: false },
    rowActionsDisplayMode: { default: 'on-hover' }
  },
  emits: ["click", "update:checked-rows", "page-change"],
  setup(__props: any, { expose, emit }: { emit: ({
  (e: 'click'): void;
  (e: 'update:checked-rows', data: any[]): void;
  (e: 'page-change', page: number): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { data: any[], paginated: boolean, rowsPerPage: number, total?: number, paginationSimple: boolean, paginationPosition: PaginationPosition, defaultSortDirection: SortOrder, sortIcon: string, sortIconSize: SortIconSize, checkable: boolean, checkedRows?: any[], filterMatchers?: Record<string, FilterMatcher>, defaultSort?: string | string[], titleField?: string, titleLabel?: string, loading: boolean, hoverable: boolean, detailed: boolean, detailKey?: string, customDetailRow: boolean, searchable: boolean, stickyHeaders: boolean, detailRowActions: boolean, backendPagination: boolean, rowActionsDisplayMode: 'on-hover' | 'always' };





const $slots = useSlots();
const { t } = useI18n();

const filteredData = ref<any[]>([]);
const searchText = ref('');
const columnSearchMatchers = ref<SearchMatcher[]>(getColumnSearchMatchers());
const areFiltersOpenInternal = ref(false);
const isRowMenuOpen = ref(false);
const areFiltersApplying = ref(false);
const isSearchBoxActive = ref(false);
const filterValues = shallowRef<Record<string, any>>({});
const tableRef = ref<any>(null);

watch(
  () => props.data,
  () => {
    if (areFiltersApplying.value) {
      // If loading the page with filters defined in the URL
      filteredData.value = filterData(filterValues.value);
    } else {
      filteredData.value = props.data;
    }
    searchText.value = '';
  },
  { immediate: true },
);

watch(
  () => props.data,
  () => {
    emit('update:checked-rows', []);
  },
);

function onSearchTextChanged(newSearchText: string) {
  searchText.value = newSearchText;
  filteredData.value = searchData(newSearchText);
}

function searchData(newSearchText: string): any[] {
  return props.data.filter((dataItem) =>
    columnSearchMatchers.value.some((matches) =>
      matches(dataItem, newSearchText),
    ),
  );
}

function onFiltersChanged(
  newFilterValues: Record<string, any>,
  areApplying: boolean,
) {
  filteredData.value = filterData(newFilterValues);
  areFiltersOpenInternal.value = false;
  filterValues.value = newFilterValues;
  areFiltersApplying.value = areApplying;
}

function filterData(newFilterValues: Record<string, any>): any[] {
  return props.data.filter((dataItem) =>
    Object.entries(newFilterValues).every(([filterKey, filterValue]) => {
      const matches = props.filterMatchers![filterKey];
      return matches && matches(dataItem, filterValue);
    }),
  );
}

function getColumnSearchMatchers() {
  const slots = $slots.default?.() ?? [];
  return slots
    ?.filter((slot: any) => !isNil(slot.componentOptions))
    .map((slot: any) => slot.componentOptions.propsData)
    .map(
      (slotProps: any) =>
        slotProps.customSearch ?? createDefaultStringMatcher(slotProps.field),
    );
}

function onRowClicked(row: any) {
  if (!isRowMenuOpen.value) {
    emit('click', row);
  }
}

/** Prevents the row click to be triggered when the row menu is toggled. */
function workaroundClickPropagationIssue(isOpen: boolean) {
  if (isOpen) {
    isRowMenuOpen.value = true;
  } else {
    // If we changed isRowMenuOpen instantly, closing the menu by clicking the
    // menu trigger would trigger the row click.
    setTimeout(() => (isRowMenuOpen.value = false));
  }
}

function createDefaultStringMatcher(fieldPath: string) {
  // FIXME: We should match against the actual table cell content instead of the
  //   object value.
  return (dataItem: any | null, searchText: string) =>
    matchIgnoringCase(
      getFieldPathValue(dataItem, fieldPath)?.toString(),
      searchText,
    );
}

function areFiltersOpen(): boolean {
  return areFiltersOpenInternal.value;
}
expose({ areFiltersOpen });

const hasFilters = computed<boolean>(() => !isNil($slots.filters));

const hasSelectionActions = computed<boolean>(
  () => !isNil($slots['selection-actions']),
);

const hasRowActions = computed<boolean>(() => !isNil($slots['row-actions']));

const hasTableActionsInToolbar = computed<boolean>(
  () => !isNil($slots['toolbar-actions']),
);

const defaultTitleLabel = computed<string>(() => t('form.name').toString());

const hasBottomLeftSlot = computed<boolean>(() => 'bottom-left' in $slots);

const hasDetailsSlot = computed<boolean>(() => 'detail' in $slots);

const showFilters = computed<boolean>(
  () => hasFilters.value && !isSearchBoxActive.value,
);

const showToolbarRight = computed<boolean>(
  () => showFilters.value || hasSelectionActions.value,
);

return { __sfc: true,props, emit, $slots, t, filteredData, searchText, columnSearchMatchers, areFiltersOpenInternal, isRowMenuOpen, areFiltersApplying, isSearchBoxActive, filterValues, tableRef, onSearchTextChanged, searchData, onFiltersChanged, filterData, getColumnSearchMatchers, onRowClicked, workaroundClickPropagationIssue, createDefaultStringMatcher, areFiltersOpen, hasFilters, hasSelectionActions, hasRowActions, hasTableActionsInToolbar, defaultTitleLabel, hasBottomLeftSlot, hasDetailsSlot, showFilters, showToolbarRight, TableActionsList, SearchBox, ControlledSidebar, TableColumnActionsWrapper }
}

})